/** What a cell needs to know when its editor opens. */
export interface TrackerCellSaveState {
  hasPendingSave: boolean;
  generation: number;
}

/** Where an edit started: the server value shown (undefined when that was an unsaved edit) and the cell's conflict generation. */
export interface TrackerCellEditStart {
  value: unknown;
  generation: number;
}

/** "stale": the editor opened before a conflict on the cell; "superseded": queued behind a save of the cell that hit a conflict. */
export type TrackerSaveOutcome = "saved" | "stale" | "superseded";

interface TrackerCellEditState {
  confirmedValue: unknown;
  confirmedEdit: number;
  pendingValues: Map<number, unknown>;
}

/** Tracks optimistic edits per cell; after a save settles the cell shows its newest pending edit or the last server value, and a conflict drops newer edits. */
export class TrackerCellEdits {
  private readonly cells = new Map<string, TrackerCellEditState>();
  // Bumped on every conflict and never reset, so an edit started before a conflict can tell it is stale.
  private readonly generations = new Map<string, number>();
  private sequence = 0;

  /** Records an edit; with nothing pending, baseValue is the server value the edit started from. */
  public begin(cellKey: string, baseValue: unknown, newValue: unknown): number {
    this.sequence += 1;
    const state = this.cells.get(cellKey);
    if (!state || state.pendingValues.size === 0) {
      this.cells.set(cellKey, { confirmedValue: baseValue, confirmedEdit: 0, pendingValues: new Map([[this.sequence, newValue]]) });
    } else {
      state.pendingValues.set(this.sequence, newValue);
    }
    return this.sequence;
  }

  public static key(campaignId: string, field: string): string {
    return `${campaignId}:${field}`;
  }

  public saveState(cellKey: string): TrackerCellSaveState {
    return { hasPendingSave: (this.cells.get(cellKey)?.pendingValues.size ?? 0) > 0, generation: this.generation(cellKey) };
  }

  public generation(cellKey: string): number {
    return this.generations.get(cellKey) ?? 0;
  }

  /** Returns the value the cell should show now. */
  public succeed(cellKey: string, editNumber: number): unknown {
    const state = this.cells.get(cellKey);
    if (!state) return undefined;
    const savedValue = state.pendingValues.get(editNumber);
    state.pendingValues.delete(editNumber);
    if (editNumber > state.confirmedEdit) {
      state.confirmedValue = savedValue;
      state.confirmedEdit = editNumber;
    }
    return TrackerCellEdits.shown(state);
  }

  /** A 409 tells the server's current value and starts a new conflict generation; newer edits of the cell will not be sent, so the cell shows that value. */
  public reject(cellKey: string, editNumber: number, serverValue: unknown): unknown {
    this.generations.set(cellKey, this.generation(cellKey) + 1);
    const state = this.cells.get(cellKey);
    if (!state) return serverValue;
    for (const pendingEdit of state.pendingValues.keys()) {
      if (pendingEdit >= editNumber) state.pendingValues.delete(pendingEdit);
    }
    state.confirmedValue = serverValue;
    state.confirmedEdit = Math.max(state.confirmedEdit, editNumber);
    return TrackerCellEdits.shown(state);
  }

  /** The last value the server accepted for the cell, which the next save must expect. */
  public confirmed(cellKey: string): unknown {
    return this.cells.get(cellKey)?.confirmedValue;
  }

  /** Returns the value the cell should show now. */
  public fail(cellKey: string, editNumber: number): unknown {
    const state = this.cells.get(cellKey);
    if (!state) return undefined;
    state.pendingValues.delete(editNumber);
    return TrackerCellEdits.shown(state);
  }

  private static shown(state: TrackerCellEditState): unknown {
    const newestPending = Math.max(0, ...state.pendingValues.keys());
    return newestPending > state.confirmedEdit ? state.pendingValues.get(newestPending) : state.confirmedValue;
  }
}
